import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag, FlagOff, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

const inp = "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-600 focus:outline-none focus:border-amber-500/50 transition";

export default function TaskFlagDialog({ device, onClose, onSaved, de = true }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const flagged = !!(device && device.task_flag);
  useEffect(() => { if (device) setNote(device.task_note || ""); }, [device]);
  if (!device) return null;
  const ident = device.asset_tag || device.device_code || device.device_uid;

  const save = async (flag) => {
    if (flag && !note.trim()) { toast.error(de ? "Bitte eine Notiz zur Aufgabe eingeben." : "Please enter a note."); return; }
    setBusy(true);
    try {
      await api.patch(`/devices/${encodeURIComponent(ident)}`, { task_flag: flag, task_note: flag ? note.trim() : "" });
      toast.success(flag ? (de ? "Als offene Aufgabe markiert." : "Flagged as open task.") : (de ? "Markierung entfernt." : "Flag removed."));
      onSaved && onSaved(); onClose && onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!device} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent data-testid="task-flag-dialog" className="max-w-lg bg-gray-50 border-gray-200 text-gray-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Flag className="w-5 h-5 text-amber-600" /> {de ? "Aufgabe markieren" : "Flag task"}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {de ? "Markiere dieses Gerät als offene Aufgabe. Es erscheint im Bericht, bis die Aufgabe erledigt ist." : "Flag this device as an open task; it appears in the report until resolved."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-gray-700">
            <span className="font-mono text-gray-900">{device.device_code || device.asset_tag || "—"}</span>
            {device.location_name ? <span className="text-gray-600"> · {device.location_name}</span> : null}
          </div>
          <label>
            <span className="block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1">{de ? "Notiz / Was stimmt nicht?" : "Note / what is wrong?"}</span>
            <textarea data-testid="task-flag-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4} className={inp} placeholder={de ? "z. B. falsche Seriennummer, Gerät reagiert nicht, Standort prüfen …" : "e.g. wrong serial, device not responding …"} />
          </label>
        </div>
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
          {flagged ? (
            <button data-testid="task-flag-resolve" onClick={() => save(false)} disabled={busy}
              className="flex items-center gap-2 bg-[#c00000]/15 border border-[#c00000]/40 text-[#c00000] hover:bg-[#c00000]/25 rounded-lg px-3 py-2 text-sm transition-colors disabled:opacity-50">
              <CheckCircle2 className="w-4 h-4" /> {de ? "Erledigt – Markierung entfernen" : "Resolve – remove flag"}
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="bg-gray-50 border border-gray-200 text-gray-700 rounded-lg px-3 py-2 text-sm hover:bg-gray-100 transition-colors">{de ? "Abbrechen" : "Cancel"}</button>
            <button data-testid="task-flag-save" onClick={() => save(true)} disabled={busy}
              className="flex items-center gap-2 bg-amber-500/90 hover:bg-amber-500 text-black font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />} {flagged ? (de ? "Notiz speichern" : "Save note") : (de ? "Markieren" : "Flag")}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
